import { useState } from 'react'
import { api } from '../api'
import { Icon } from './Icon'

/**
 * Help with the words, on the text a writer selected (§22).
 *
 * The rule this component exists to keep is the spec's: "AI output should be
 * previewed as a suggestion/diff and remain editable." So nothing here writes
 * to the page. A suggestion arrives in a box the writer can edit, beside what
 * they had, and it reaches the document only when they press the button that
 * says so — and it replaces the selection, which is the only text that was
 * ever sent.
 *
 * It appears only when there IS a selection. An assistant that offers to
 * improve nothing in particular ends up rewriting whole pages, which is the
 * failure mode this feature has everywhere else it exists.
 */
/**
 * The languages Translate offers.
 *
 * A select rather than a text box, and the reason is in `writing.ts`: the target
 * language is written into the model's INSTRUCTION, which is the one place a
 * free string could steer it. The API refuses anything that is not on ITS list.
 *
 * Written out here rather than imported from `@staleless/core`, which was tried:
 * importing it pulls the whole package — Postgres driver, node crypto, streams —
 * into the browser bundle, and the build says so. Two lists that are never
 * compared do not stay the same, so `writing-languages.test.ts` holds this one
 * against the core's.
 */
const LANGUAGES = [
  'Arabic',
  'Simplified Chinese',
  'Dutch',
  'French',
  'German',
  'Hindi',
  'Italian',
  'Japanese',
  'Korean',
  'Brazilian Portuguese',
  'Spanish',
  'Turkish',
] as const

const ACTIONS = [
  { id: 'improve', label: 'Improve' },
  { id: 'shorten', label: 'Shorten' },
  { id: 'expand', label: 'Expand' },
  { id: 'simplify', label: 'Simplify' },
  { id: 'grammar', label: 'Fix grammar' },
  { id: 'tone', label: 'Match the tone' },
  { id: 'summarise', label: 'Summarise' },
  { id: 'outline', label: 'Outline it' },
  { id: 'draft', label: 'Draft from notes' },
  { id: 'example', label: 'Add an example' },
  { id: 'explain', label: 'Explain this code' },
] as const

interface Props {
  /**
   * The selected words as plain text, empty when nothing is selected.
   *
   * Only used to decide whether there is anything to help with, and to show the
   * writer what they picked. What is SENT comes from `markdownOf` — the block
   * editor's selection is a document fragment, and sending its plain text would
   * throw away the writer's links and emphasis before the model ever saw them.
   */
  selection: string
  branch: string | null
  /** The selection as Markdown. Async because the block editor loads a bridge. */
  markdownOf: () => Promise<string>
  /** Replace the selection with this text. The only way anything is applied. */
  onApply: (next: string) => void | Promise<void>
}

export function WritingHelp({ selection, branch, markdownOf, onApply }: Props) {
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  /**
   * What the model SAID, which is not the same channel as what went wrong.
   *
   * "It had nothing to change" was written through `setError`, so an answer
   * came out in the danger colour behind an alert icon, and `role="alert"`
   * made a screen reader interrupt to report a success.
   */
  const [note, setNote] = useState<string | null>(null)
  /** The suggestion, as the writer may have edited it. */
  const [draft, setDraft] = useState<string | null>(null)
  const [asked, setAsked] = useState<{ action: string; before: string } | null>(null)

  const ask = async (action: string, label: string, language?: string) => {
    setBusy(action)
    setError(null)
    setNote(null)
    try {
      const text = await markdownOf()
      const said = await api.writingSuggestion(action, text, branch ?? undefined, language)
      setAsked({ action: label, before: said.before })
      setDraft(said.after)
      /**
       * Shown either way, and said as a fact rather than as a failure: the
       * model reading a sentence and finding nothing to fix is an answer.
       */
      if (said.unchanged) setNote('It had nothing to change.')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setDraft(null)
    } finally {
      setBusy(null)
    }
  }

  const close = () => {
    setDraft(null)
    setAsked(null)
    setError(null)
    setNote(null)
  }

  if (!selection.trim() && draft === null) return null

  return (
    <div className="writing" data-writing>
      {draft === null ? (
        <>
          <span className="writing__lead">
            <Icon name="text" size={12} /> Help with the selection
          </span>
          {ACTIONS.map((action) => (
            <button
              key={action.id}
              className="btn btn--ghost btn--sm"
              data-writing-action={action.id}
              disabled={busy !== null}
              onClick={() => void ask(action.id, action.label)}
            >
              {busy === action.id ? 'Thinking…' : action.label}
            </button>
          ))}
          {/*
            Translate is the one action that needs an answer before it can run,
            so it IS the control: choosing a language asks for it. A button that
            then opened a menu would be two presses for one decision.
          */}
          <select
            className="writing__lang"
            /**
             * Named, because a select with only an empty first option has no
             * accessible name at all — axe's `select-name`, found the first time
             * this panel was ever audited. "Translate…" is the placeholder a
             * sighted person reads; this is the same thing said out loud.
             */
            aria-label="Translate the selection into another language"
            data-writing-language
            defaultValue=""
            disabled={busy !== null}
            onChange={(e) => {
              const language = e.target.value
              e.target.value = ''
              if (language) void ask('translate', `Translated into ${language}`, language)
            }}
          >
            <option value="">Translate…</option>
            {LANGUAGES.map((language) => (
              <option key={language} value={language}>
                {language}
              </option>
            ))}
          </select>
          <span className="writing__note">
            Sent to our model provider to be answered. Nothing changes until you say so.
          </span>
        </>
      ) : (
        <div className="writing__panel">
          <p className="writing__lead">
            {asked?.action ?? 'Suggestion'} — edit it if you like, then use it or throw it away.
          </p>
          <details className="writing__before">
            <summary>What you selected</summary>
            <pre>{asked?.before}</pre>
          </details>
          <textarea
            className="writing__draft"
            aria-label="The suggested text, which you can edit before using it"
            data-writing-draft
            rows={Math.min(18, Math.max(4, draft.split('\n').length + 1))}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="writing__row">
            <button
              className="btn btn--primary btn--sm"
              data-writing-apply
              onClick={() => {
                void Promise.resolve(onApply(draft))
                  .then(close)
                  .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
              }}
            >
              Replace the selection
            </button>
            <button className="btn btn--ghost btn--sm" data-writing-discard onClick={close}>
              Discard
            </button>
          </div>
        </div>
      )}
      {note && (
        <p className="writing__said">
          <Icon name="info" size={11} /> {note}
        </p>
      )}
      {error && (
        <p className="writing__error" role="alert">
          <Icon name="alert" size={11} /> {error}
        </p>
      )}
    </div>
  )
}
